import { NOTIFICATIONS_ENABLED } from './app';

// Lazy route import thunks, extracted from routes.tsx so link components can
// warm a route's chunk on hover/focus without importing the route table
// (routes.tsx statically imports the home page, so importing it from a home
// card would be circular). routes.tsx wraps these same thunks in lazy(), so
// the chunk graph is unchanged — Vite splits on the import() calls below
// exactly as it did when they were inlined.
export const PAGE_LOADERS = {
  'btc-5m': () => import('@/client/pages/btc-5m'),
  'activity-rewards-live': () => import('@/client/pages/activity-rewards-live'),
  error: () => import('@/client/pages/error'),
  event: () => import('@/client/pages/event'),
  'how-it-works': () => import('@/client/pages/how-it-works'),
  leaderboard: () => import('@/client/pages/leaderboard'),
  market: () => import('@/client/pages/market'),
  pdf: () => import('@/client/pages/pdf'),
  preview: () => import('@/client/pages/preview'),
  user: () => import('@/client/pages/user'),
  'user-auth-redirect': () => import('@/client/pages/user/auth-redirect'),
  'user-invite-earn': () => import('@/client/pages/user/invite-earn'),
  // Notifications off (BON-249): no notification route, so this loader opens
  // the not-found page and the build drops the notification page chunk.
  'user-notification': () =>
    NOTIFICATIONS_ENABLED ? import('@/client/pages/user/notification') : import('@/client/pages/error'),
  'user-settings': () => import('@/client/pages/user/settings'),
};

export type PageLoaderName = keyof typeof PAGE_LOADERS;

// Factory so tests can inject fake loaders; app code uses the singleton below.
export const createRoutePrefetcher = <Name extends string>(loaders: Record<Name, () => Promise<unknown>>) => {
  const requested = new Set<Name>();
  return (name: Name) => {
    if (requested.has(name)) return;
    requested.add(name);
    // Swallow rejections (offline, flaky network): clicking through will retry
    // the import itself, and dropping the guard lets the next hover retry too.
    loaders[name]().catch(() => requested.delete(name));
  };
};

// Fire-and-forget chunk warmup for hover/focus/touchstart intent signals.
export const prefetchRoute = createRoutePrefetcher(PAGE_LOADERS);
